import { z } from 'zod'

// Generates the 32 Zod fields for course n (1–8).
const courseFields = (n) => ({
  [`course${n}Title`]:                z.string(),
  [`course${n}Code`]:                 z.string(),
  [`course${n}Teacher`]:              z.string(),
  [`course${n}ESLELD`]:               z.string(),
  [`course${n}IEP`]:                  z.string(),
  [`course${n}French`]:               z.string(),
  [`course${n}SHSM`]:                 z.string(),
  [`course${n}FirstPercentageMark`]:  z.string(),
  [`course${n}FirstMedian`]:          z.string(),
  [`course${n}FirstCreditEarned`]:    z.string(),
  [`course${n}FirstResponsibility`]:  z.string(),
  [`course${n}FirstOrganization`]:    z.string(),
  [`course${n}FirstIndependentWork`]: z.string(),
  [`course${n}FirstCollaboration`]:   z.string(),
  [`course${n}FirstInitiative`]:      z.string(),
  [`course${n}FirstSelfRegulation`]:  z.string(),
  [`course${n}FirstClassesMissed`]:   z.string(),
  [`course${n}FirstTotalClasses`]:    z.string(),
  [`course${n}FirstTimesLate`]:       z.string(),
  [`course${n}FinalPercentageMark`]:  z.string(),
  [`course${n}FinalMedian`]:          z.string(),
  [`course${n}FinalCreditEarned`]:    z.string(),
  [`course${n}FinalResponsibility`]:  z.string(),
  [`course${n}FinalOrganization`]:    z.string(),
  [`course${n}FinalIndependentWork`]: z.string(),
  [`course${n}FinalCollaboration`]:   z.string(),
  [`course${n}FinalInitiative`]:      z.string(),
  [`course${n}FinalSelfRegulation`]:  z.string(),
  [`course${n}FinalClassesMissed`]:   z.string(),
  [`course${n}FinalTotalClasses`]:    z.string(),
  [`course${n}FinalTimesLate`]:       z.string(),
  [`course${n}Comments`]:             z.string(),
})

export const formSchema = z.object({
  // ── Student info (single inputs mapped to multiple PDF locations by fill.js) ─
  semester:           z.string(),
  reportingPeriod:    z.string(),
  date:               z.string(),
  studentName:        z.string(),
  oen:                z.string(),
  grade:              z.string(),
  homeroom:           z.string(),
  principal:          z.string(),
  studentAddress:     z.string(),
  schoolCouncilChair: z.string(),

  // ── School / board info ───────────────────────────────────────────────────
  schoolName:        z.string(),
  schoolTelephone:   z.string(),
  schoolAddress:     z.string(),
  schoolFax:         z.string(),
  boardName:         z.string(),
  boardEmailWebsite: z.string(),
  boardAddress:      z.string(),

  // ── Courses 1–4 (Semester 1) ──────────────────────────────────────────────
  ...courseFields(1),
  ...courseFields(2),
  ...courseFields(3),
  ...courseFields(4),

  // ── Courses 5–8 (Semester 2) ──────────────────────────────────────────────
  ...courseFields(5),
  ...courseFields(6),
  ...courseFields(7),
  ...courseFields(8),

  // ── Credit Summary (page 3 of the PDF) ───────────────────────────────────
  compulsoryCreditsEarnedThisReport:         z.string(),
  compulsoryCreditsEarnedToDate:             z.string(),
  englishEarnedThisReport:                   z.string(),
  englishEarnedToDate:                       z.string(),
  frenchEarnedThisReport:                    z.string(),
  frenchEarnedToDate:                        z.string(),
  mathematicsEarnedThisReport:               z.string(),
  mathematicsEarnedToDate:                   z.string(),
  scienceEarnedThisReport:                   z.string(),
  scienceEarnedToDate:                       z.string(),
  canadianHistoryEarnedThisReport:           z.string(),
  canadianHistoryEarnedToDate:               z.string(),
  canadianGeographyEarnedThisReport:         z.string(),
  canadianGeographyEarnedToDate:             z.string(),
  theArtsEarnedThisReport:                   z.string(),
  theArtsEarnedToDate:                       z.string(),
  healthPhysicalEducationEarnedThisReport:   z.string(),
  healthPhysicalEducationEarnedToDate:       z.string(),
  civicsEarnedThisReport:                    z.string(),
  civicsEarnedToDate:                        z.string(),
  careerEarnedThisReport:                    z.string(),
  careerEarnedToDate:                        z.string(),
  group1EarnedThisReport:                    z.string(),
  group1EarnedToDate:                        z.string(),
  group2EarnedThisReport:                    z.string(),
  group2EarnedToDate:                        z.string(),
  group3EarnedThisReport:                    z.string(),
  group3EarnedToDate:                        z.string(),
  optionalCreditsEarnedThisReport:           z.string(),
  optionalCreditsEarnedToDate:               z.string(),
  totalCreditsRequiredEarnedThisReport:      z.string(),
  totalCreditsRequiredEarnedToDate:          z.string(),
  communityInvolvementEarnedThisReport:      z.string(),
  communityInvolvementEarnedToDate:          z.string(),
  onlineLearningRequirementEarnedThisReport: z.string(),
  onlineLearningRequirementEarnedToDate:     z.string(),
  specialistHighSkillsMajorName:             z.string(),
  specialistHighSkillsMajorTotalRequired:    z.string(),
  specialistHighSkillsMajorEarnedThisReport: z.string(),
  specialistHighSkillsMajorEarnedToDate:     z.string(),
  oSSLiteracyRequirementCompleted:           z.string(),
  schoolUse:                                 z.string(),
  principalSignatureDate:                    z.string(),

  _completedFields: z.array(z.string()).default([]),
})

export const defaultValues = {
  ...Object.fromEntries(
    Object.keys(formSchema.shape)
      .filter((k) => k !== '_completedFields')
      .map((k) => [k, ''])
  ),
  _completedFields: [],
}
